import { movement } from "./movement.js";
const linkTableCache = new WeakMap();
function linkTable(asset) {
    const cached = linkTableCache.get(asset);
    if (cached)
        return cached;
    const offsets = new Int32Array(asset.areas.length + 1);
    asset.areas.forEach((area, index) => {
        offsets[index + 1] = offsets[index] + area.links.length;
    });
    const count = offsets[asset.areas.length];
    // [fromX, fromY, landX, landY] je Link
    const midpoints = new Float64Array(count * 4);
    asset.areas.forEach((area, areaId) => {
        area.links.forEach((link, index) => {
            const land = link.land ?? link.from;
            const offset = (offsets[areaId] + index) * 4;
            midpoints[offset] = (link.from[0] + link.from[3]) / 2;
            midpoints[offset + 1] = (link.from[1] + link.from[4]) / 2;
            midpoints[offset + 2] = (land[0] + land[3]) / 2;
            midpoints[offset + 3] = (land[1] + land[4]) / 2;
        });
    });
    const linkArea = new Int32Array(count);
    for (let areaId = 0; areaId < asset.areas.length; areaId += 1)
        linkArea.fill(areaId, offsets[areaId], offsets[areaId + 1]);
    const table = { offsets, count, midpoints, linkArea };
    linkTableCache.set(asset, table);
    return table;
}
class MinHeap {
    keys = [];
    values = [];
    get size() {
        return this.keys.length;
    }
    push(value, key) {
        this.keys.push(key);
        this.values.push(value);
        let index = this.keys.length - 1;
        while (index > 0) {
            const parent = (index - 1) >> 1;
            if (this.keys[parent] <= key)
                break;
            this.swap(index, parent);
            index = parent;
        }
    }
    pop() {
        const top = this.values[0];
        const lastKey = this.keys.pop();
        const lastValue = this.values.pop();
        if (this.keys.length > 0) {
            this.keys[0] = lastKey;
            this.values[0] = lastValue;
            let index = 0;
            for (;;) {
                const left = index * 2 + 1;
                const right = left + 1;
                let smallest = index;
                if (left < this.keys.length && this.keys[left] < this.keys[smallest])
                    smallest = left;
                if (right < this.keys.length &&
                    this.keys[right] < this.keys[smallest])
                    smallest = right;
                if (smallest === index)
                    break;
                this.swap(index, smallest);
                index = smallest;
            }
        }
        return top;
    }
    swap(left, right) {
        [this.keys[left], this.keys[right]] = [this.keys[right], this.keys[left]];
        [this.values[left], this.values[right]] = [
            this.values[right],
            this.values[left],
        ];
    }
}
function areaSpeed(asset, areaId) {
    return asset.areas[areaId].crouch
        ? movement.runSpeedHuPerSecond * movement.crouchSpeedFactor
        : movement.runSpeedHuPerSecond;
}
export function findNavigationRoute(asset, start, goal) {
    const hu = asset.huPerRadarUnit;
    const walkSeconds = (areaId, ax, ay, bx, by) => (Math.hypot(bx - ax, by - ay) * hu) / areaSpeed(asset, areaId);
    if (start.areaId === goal.areaId)
        return {
            steps: [],
            seconds: walkSeconds(start.areaId, start.x, start.y, goal.x, goal.y),
        };
    const { offsets, count, midpoints, linkArea } = linkTable(asset);
    const heuristic = (x, y) => (Math.hypot(goal.x - x, goal.y - y) * hu) / movement.runSpeedHuPerSecond;
    const goalNode = count;
    const best = new Float64Array(count + 1).fill(Infinity);
    const previous = new Int32Array(count + 1).fill(-1);
    const open = new MinHeap();
    const relaxFrom = (areaId, x, y, base, origin) => {
        if (areaId === goal.areaId) {
            const cost = base + walkSeconds(areaId, x, y, goal.x, goal.y);
            if (cost < best[goalNode]) {
                best[goalNode] = cost;
                previous[goalNode] = origin;
                open.push(goalNode, cost);
            }
        }
        const area = asset.areas[areaId];
        for (let index = 0; index < area.links.length; index += 1) {
            const id = offsets[areaId] + index;
            const cost = base +
                walkSeconds(areaId, x, y, midpoints[id * 4], midpoints[id * 4 + 1]) +
                area.links[index].cost;
            if (cost < best[id]) {
                best[id] = cost;
                previous[id] = origin;
                open.push(id, cost + heuristic(midpoints[id * 4 + 2], midpoints[id * 4 + 3]));
            }
        }
    };
    relaxFrom(start.areaId, start.x, start.y, 0, -1);
    const closed = new Uint8Array(count + 1);
    while (open.size > 0) {
        const node = open.pop();
        if (closed[node])
            continue;
        closed[node] = 1;
        if (node === goalNode)
            break;
        const toArea = asset.areas[linkArea[node]].links[node - offsets[linkArea[node]]].to;
        relaxFrom(toArea, midpoints[node * 4 + 2], midpoints[node * 4 + 3], best[node], node);
    }
    if (!Number.isFinite(best[goalNode]))
        return null;
    const steps = [];
    for (let node = previous[goalNode]; node >= 0; node = previous[node]) {
        const fromArea = linkArea[node];
        steps.push({
            fromArea,
            link: asset.areas[fromArea].links[node - offsets[fromArea]],
        });
    }
    steps.reverse();
    return { steps, seconds: best[goalNode] };
}
